// @ts-strict-ignore
import queryString from 'query-string';
import {getAppRouter} from '#iron/routes/router';
import type {To} from 'react-router-dom';
import type {RouterNavigateOptions} from '@remix-run/router';

const VALID_TLDS = [
  `aero`,
  `biz`,
  `cat`,
  `com`,
  `coop`,
  `edu`,
  `gov`,
  `info`,
  `int`,
  `jobs`,
  `mil`,
  `mobi`,
  `museum`,
  `name`,
  `net`,
  `org`,
  `travel`,
  `ac`,
  `ad`,
  `ae`,
  `af`,
  `ag`,
  `ai`,
  `al`,
  `am`,
  `an`,
  `ao`,
  `aq`,
  `ar`,
  `as`,
  `at`,
  `au`,
  `aw`,
  `az`,
  `ba`,
  `bb`,
  `bd`,
  `be`,
  `bf`,
  `bg`,
  `bh`,
  `bi`,
  `bj`,
  `bm`,
  `bn`,
  `bo`,
  `br`,
  `bs`,
  `bt`,
  `bv`,
  `bw`,
  `by`,
  `bz`,
  `ca`,
  `cc`,
  `cd`,
  `cf`,
  `cg`,
  `ch`,
  `ci`,
  `ck`,
  `cl`,
  `cm`,
  `cn`,
  `co`,
  `cr`,
  `cs`,
  `cu`,
  `cv`,
  `cx`,
  `cy`,
  `cz`,
  `de`,
  `dj`,
  `dk`,
  `dm`,
  `do`,
  `dz`,
  `ec`,
  `ee`,
  `eg`,
  `eh`,
  `er`,
  `es`,
  `et`,
  `eu`,
  `fi`,
  `fj`,
  `fk`,
  `fm`,
  `fo`,
  `fr`,
  `ga`,
  `gb`,
  `gd`,
  `ge`,
  `gf`,
  `gg`,
  `gh`,
  `gi`,
  `gl`,
  `gm`,
  `gn`,
  `gp`,
  `gq`,
  `gr`,
  `gs`,
  `gt`,
  `gu`,
  `gw`,
  `gy`,
  `hk`,
  `hm`,
  `hn`,
  `hr`,
  `ht`,
  `hu`,
  `id`,
  `ie`,
  `il`,
  `im`,
  `in`,
  `io`,
  `iq`,
  `ir`,
  `is`,
  `it`,
  `je`,
  `jm`,
  `jo`,
  `jp`,
  `ke`,
  `kg`,
  `kh`,
  `ki`,
  `km`,
  `kn`,
  `kp`,
  `kr`,
  `kw`,
  `ky`,
  `kz`,
  `la`,
  `lb`,
  `lc`,
  `li`,
  `lk`,
  `lr`,
  `ls`,
  `lt`,
  `lu`,
  `lv`,
  `ly`,
  `ma`,
  `mc`,
  `md`,
  `mg`,
  `mh`,
  `mk`,
  `ml`,
  `mm`,
  `mn`,
  `mo`,
  `mp`,
  `mq`,
  `mr`,
  `ms`,
  `mt`,
  `mu`,
  `mv`,
  `mw`,
  `mx`,
  `my`,
  `mz`,
  `na`,
  `nc`,
  `ne`,
  `nf`,
  `ng`,
  `ni`,
  `nl`,
  `no`,
  `np`,
  `nr`,
  `nu`,
  `nz`,
  `om`,
  `pa`,
  `pe`,
  `pf`,
  `pg`,
  `ph`,
  `pk`,
  `pl`,
  `pm`,
  `pn`,
  `pr`,
  `ps`,
  `pt`,
  `pw`,
  `py`,
  `qa`,
  `re`,
  `ro`,
  `ru`,
  `rw`,
  `sa`,
  `sb`,
  `sc`,
  `sd`,
  `se`,
  `sg`,
  `sh`,
  `si`,
  `sj`,
  `sk`,
  `sl`,
  `sm`,
  `sn`,
  `so`,
  `sr`,
  `st`,
  `su`,
  `sv`,
  `sy`,
  `sz`,
  `tc`,
  `td`,
  `tf`,
  `tg`,
  `th`,
  `tj`,
  `tk`,
  `tm`,
  `tn`,
  `to`,
  `tp`,
  `tr`,
  `tt`,
  `tv`,
  `tw`,
  `tz`,
  `ua`,
  `ug`,
  `uk`,
  `um`,
  `us`,
  `uy`,
  `uz`,
  `va`,
  `vc`,
  `ve`,
  `vg`,
  `vi`,
  `vn`,
  `vu`,
  `wf`,
  `ws`,
  `ye`,
  `yt`,
  `yu`,
  `za`,
  `zm`,
  `zr`,
  `zw`,
];

const TLD_REGEX = new RegExp(`\\.(${VALID_TLDS.join(`|`)})\\b`);
const HTTP_PROTOCOL_REGEX = /^https?:\/\//;

export function addProtocolIfAbsent(url: string): string {
  if (!HTTP_PROTOCOL_REGEX.test(url)) {
    return `http://` + url;
  }

  return url;
}

export function isURL(str) {
  if (typeof str !== `string` || str.includes(` `)) {
    return false;
  }
  const hasHTTPishProtocol = HTTP_PROTOCOL_REGEX.test(str);
  const hasNonHTTPishProtocol = /^.+:/s.test(str) && !hasHTTPishProtocol;
  const hasFTPProtocol = str.startsWith(`ftp://`);
  const maybeEmail = !hasHTTPishProtocol && !hasFTPProtocol && str.includes(`@`);
  const maybeUrl = hasHTTPishProtocol || TLD_REGEX.test(str);
  return maybeUrl && !maybeEmail && !hasNonHTTPishProtocol;
}

function parseFeatureFlagsOverridesStr(featureFlagsStr) {
  if (!featureFlagsStr) {
    return [];
  }
  return featureFlagsStr.split(`,`);
}

export function getFeatureFlagUrlOverrides() {
  const urlOptions = queryString.parse(getLocation().search);
  const enabledFeatureFlags = parseFeatureFlagsOverridesStr(urlOptions[`feature-flags`]);
  const disabledFeatureFlags = parseFeatureFlagsOverridesStr(urlOptions[`disable-feature-flags`]);
  return Object.fromEntries([
    ...enabledFeatureFlags.map((flag) => [flag, true]),
    ...disabledFeatureFlags.map((flag) => [flag, false]),
  ]);
}

export function parseUrl(url: string): {host: string; pathname: string; url: string} {
  let sanitizedUrl = !url ? getLocation().href : url;
  if (sanitizedUrl.startsWith(`/`) && !sanitizedUrl.startsWith(`//`)) {
    sanitizedUrl = `${window.location.origin}${sanitizedUrl}`;
  } else if (sanitizedUrl.startsWith(`//`)) {
    sanitizedUrl = `${window.location.protocol}${sanitizedUrl}`;
  }

  const {pathname, host, href} = new URL(sanitizedUrl);
  return {
    host,
    pathname,
    url: href,
  };
}

/**
 * True when `url`, resolved against the current origin, is HTTPS or same-origin HTTP. The HTTP
 * exception preserves local development without allowing navigation to insecure external URLs.
 */
export function hasNavigableProtocol(url: string | URL): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.protocol === `https:` || (parsed.protocol === `http:` && parsed.origin === window.location.origin);
  } catch {
    return false;
  }
}

/**
 * True when `url`, resolved against the current origin, uses the current page's origin and protocol.
 * Validates navigation targets taken from user-controllable input such as a `next` query
 * parameter, which would otherwise allow `javascript:` URIs and open redirects to `//evil.com`.
 */
export function safeInternalPath(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.origin === window.location.origin && parsed.protocol === window.location.protocol;
  } catch {
    return false;
  }
}

export function getHost(allowLocalHost = false): string {
  const {host, protocol} = window.location;
  return host !== `localhost` || allowLocalHost ? `${protocol}//${host}` : `https://mixpanel.com`;
}

export function getHashString(): string {
  return getLocation().hash.replace(/^#/, ``);
}

export function getLocation(): URL {
  const {pathname, hash, search} = getAppRouter()?.state?.location ?? window.location;

  return new URL(window.location.origin + pathname + search + hash);
}

export function navigate(to: To, {replace = false}: RouterNavigateOptions = {}) {
  if (getAppRouter()?.navigate) {
    getAppRouter()?.navigate(to, {replace});
  }
}
